import type { Recipe, Step } from '../data/types';
import { stepColor } from '../lib/stepColors';
import { StepBadge } from './StepBadge';
import { GlossaryText } from './GlossaryText';
import { scaleAmount } from '../lib/scale';
import { optionalMeta, optionalReason } from '../lib/optional';

// One numbered step. The step owns its color: rail, badge, and the chips of
// the ingredients it consumes. `critical` steps say so in words, not just weight.
export function StepCard({
  step,
  recipe,
  factor = 1,
  done = false,
  onToggle,
}: {
  step: Step;
  recipe: Recipe;
  factor?: number;
  done?: boolean;
  onToggle?: () => void;
}) {
  const c = stepColor(step.n);
  const used = step.ingredientIds
    .map((id) => {
      const ing = recipe.ingredients.find((i) => i.id === id);
      if (!ing) return null;
      const use = ing.uses.find((u) => u.step === step.n) ?? ing.uses[0];
      return { ing, use };
    })
    .filter(Boolean) as { ing: Recipe['ingredients'][number]; use: Recipe['ingredients'][number]['uses'][number] }[];

  // Optional ingredients leave the main chip row entirely. Rendering "1 tbsp
  // cocoa" in the same pill as "3 packets chili-o" told the cook they were the
  // same kind of thing, which is the one thing they are not.
  const required = used.filter(({ ing }) => !ing.optional);
  const extras = used.filter(({ ing }) => ing.optional);

  return (
    <li
      data-pdf
      className={`step-card relative rounded-xl border border-line bg-card p-4 pl-5 sm:p-5 sm:pl-6 ${done ? 'done' : ''}`}
      style={{ boxShadow: `inset 4px 0 0 ${c.base}`, ['--_rail' as never]: c.base }}
    >
      {onToggle ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={done}
          aria-label={`Step ${step.n}, ${step.title}${done ? ', done' : ''}`}
          onClick={onToggle}
          className="-m-2 flex w-[calc(100%+1rem)] cursor-pointer items-center gap-3 rounded-lg p-2 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
        >
          <StepBadge n={step.n} size="lg" />
          <h3 className="flex-1 font-serif text-xl font-semibold leading-snug">{step.title}</h3>
          {step.time && (
            <span className="whitespace-nowrap font-sans text-[0.78rem] font-medium text-ink-soft">
              {step.time}
            </span>
          )}
          <span
            aria-hidden="true"
            className={`print-hide inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
              done ? 'border-ink bg-ink text-paper' : 'bg-card'
            }`}
            style={!done ? { borderColor: c.base } : undefined}
          >
            {done && (
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            )}
          </span>
        </button>
      ) : (
        <div className="flex items-center gap-3">
          <StepBadge n={step.n} size="lg" />
          <h3 className="flex-1 font-serif text-xl font-semibold leading-snug">{step.title}</h3>
          {step.time && (
            <span className="whitespace-nowrap font-sans text-[0.78rem] font-medium text-ink-soft">
              {step.time}
            </span>
          )}
        </div>
      )}
      {step.critical && (
        <p
          className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-sans text-[0.72rem] font-bold uppercase tracking-[0.08em]"
          style={{ backgroundColor: c.tint, color: c.deep }}
        >
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
            <path d="M12 2L1 21h22L12 2zm1 14h-2v2h2v-2zm0-7h-2v5h2V9z" />
          </svg>
          Don’t skip this
        </p>
      )}
      {required.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-1.5" aria-label={`Ingredients for step ${step.n}`}>
          {required.map(({ ing, use }) => (
            <li
              key={ing.id}
              className="inline-flex items-baseline gap-1.5 rounded-full border px-2.5 py-1 font-sans text-[0.8rem]"
              style={{ backgroundColor: c.tint, borderColor: c.base, color: c.deep }}
            >
              <span className="font-semibold">{scaleAmount(use.amount, factor).text}</span>
              <span>{ing.name.toLowerCase()}</span>
            </li>
          ))}
        </ul>
      )}
      <GlossaryText
        body={step.body}
        glossary={recipe.glossary}
        ingredients={recipe.ingredients}
        factor={factor}
        accent={c.deep}
        className="mt-3 text-[1.02rem]"
      />
      {extras.length > 0 && (
        <section
          className="mt-3 rounded-lg border border-dashed border-line bg-paper/70 px-3.5 py-3"
          aria-label={`Optional additions for step ${step.n}`}
        >
          <h4 className="font-sans text-[0.68rem] font-bold uppercase tracking-[0.1em] text-ink-soft">
            Optional here — not in the main list
          </h4>
          <ul className="mt-2 space-y-2">
            {extras.map(({ ing, use }) => {
              const m = optionalMeta(ing);
              return (
                <li key={ing.id}>
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="inline-flex items-baseline gap-1.5 rounded-full border border-dashed px-2.5 py-0.5 font-sans text-[0.8rem]"
                      style={{ borderColor: m.bd, color: m.fg }}>
                      <span className="font-semibold">{scaleAmount(use.amount, factor).text}</span>
                      <span>{ing.name.toLowerCase()}</span>
                    </span>
                    <span
                      className="rounded-full px-1.5 py-px font-sans text-[0.62rem] font-bold uppercase tracking-[0.08em]"
                      style={{ backgroundColor: m.bg, color: m.fg }}
                    >
                      {m.label}
                    </span>
                  </div>
                  <p className="mt-0.5 font-serif text-[0.86rem] italic leading-snug text-ink-soft">
                    {optionalReason(ing)}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {step.tip && (
        <aside className="mt-3 border-l-2 border-line pl-3.5 font-serif text-[0.92rem] italic leading-relaxed text-ink-soft">
          <span className="font-sans text-[0.7rem] font-semibold uppercase not-italic tracking-[0.08em]">Tip </span>
          <GlossaryText
            body={step.tip}
            glossary={[]}
            ingredients={recipe.ingredients}
            factor={factor}
            accent={c.deep}
            className="inline [&>p]:inline [&>p]:font-serif [&>p]:italic"
          />
        </aside>
      )}
    </li>
  );
}
